import { ProductsId, SearchMaskMountOptions } from '@webc/meeseeks-ui-sdk';
import { relationProductsSelector, selectedProductsSelector } from 'app/store/selectors';
import { updateSearchLocation } from 'app/store/thunk/location';
import { ProductDataType } from 'app/models/types/store/product';
import { uniq } from 'lodash';
import { SearchMaskLocation } from 'app/models/types/store/location';
import { isUuid } from 'app/functions/general/isUuid';
import { setDepartureDate, setReturnDate, toggleReturnDate } from 'app/store/reducers/date';
import { updateProductQuantity } from 'app/store/thunk/product';
import { AppDispatch, RootState } from 'app/hooks/appHooks';
import { createAsyncThunk } from '@reduxjs/toolkit';

export const updateSearchMaskValues = createAsyncThunk<
  void,
  SearchMaskMountOptions['initialSearchMaskValues'],
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  'general/updateSearchMaskValues',
  async (
    searchMaskValues: SearchMaskMountOptions['initialSearchMaskValues'],
    { dispatch, getState },
  ) => {
    const state = getState();
    const departureId = searchMaskValues.departureCity;
    const arrivalId = searchMaskValues.arrivalCity;

    if (departureId) {
      const key = isUuid(departureId) ? 'uuid' : 'legacyId';
      await dispatch(
        updateSearchLocation({
          direction: 'from',
          location: {
            [key]: departureId,
            stationId: searchMaskValues?.departureStop,
            stationName: searchMaskValues?.departureStopName,
          } as SearchMaskLocation,
          isInit: true,
        }),
      );
    }

    if (arrivalId) {
      const key = isUuid(arrivalId) ? 'uuid' : 'legacyId';

      await dispatch(
        updateSearchLocation({
          direction: 'to',
          location: {
            [key]: arrivalId,
            stationId: searchMaskValues?.arrivalStop,
            stationName: searchMaskValues?.arrivalStopName,
          } as SearchMaskLocation,
          isInit: true,
        }),
      );
    }

    if (searchMaskValues.departureDate) {
      dispatch(setDepartureDate({ date: Number(searchMaskValues.departureDate) }));
    }
    if (searchMaskValues.returnDate) {
      dispatch(setReturnDate({ date: Number(searchMaskValues.returnDate) }));
      dispatch(toggleReturnDate({ isVisible: true }));
    }

    if (searchMaskValues.products) {
      const products = Object.keys(searchMaskValues.products) as ProductsId[];
      const selectedProducts: { [key in ProductsId]?: ProductDataType } =
        selectedProductsSelector(state);
      const selectedProductsIds: ProductsId[] = uniq([
        ...(Object.keys(selectedProducts) as ProductsId[]),
        ...products,
      ]);
      const relationProducts = relationProductsSelector(state);
      const finalProducts = selectedProductsIds.filter((selectedProduct) =>
        Object.keys(relationProducts).includes(selectedProduct),
      );
      // If there's no adult in product, then set adult product quantity to 0 to overide default quantity set by productType in SDK.
      if (!finalProducts.includes('adult')) {
        dispatch(updateProductQuantity({ productType: 'adult', quantity: 0 }));
      }

      for (let i = 0; i < finalProducts.length; i++) {
        const amount = searchMaskValues.products[finalProducts[i]] || 0;
        if (typeof amount !== 'undefined') {
          dispatch(updateProductQuantity({ productType: finalProducts[i], quantity: amount }));
        }
      }
    }
  },
);
